import React from 'react';
import Logo from '../Logo';
import LanguageSwitcherCompact from '../LanguageSwitcherCompact';
import { useTranslation } from 'react-i18next';

// Memory-themed navigation with warm colors
const MemoryNavbar = ({ user, onLogout, onProfileClick, onCreditsClick, showCreditsDropdown, setShowCreditsDropdown, setShowSupport, setShowOrdersDashboard, setShowAdminPanel, setShowReferralDashboard, currentView, setCurrentView, projects, setProjectSubtab, setShowArchived, setShowPendingSubtab, setShowFailedSubtab, picturesContentRef, animatedMomentsContentRef, pendingInvitationsCount = 0, totalPendingAccessRequests = 0 }) => {
  const { t } = useTranslation();
  const [showProfileDropdown, setShowProfileDropdown] = React.useState(false);
  
  // Debug: Log when totalPendingAccessRequests changes
  React.useEffect(() => {
    console.log('🔔 MemoryNavbar totalPendingAccessRequests changed:', totalPendingAccessRequests);
  }, [totalPendingAccessRequests]);
  
  // Get user initials and color for avatar
  const getUserInitial = () => {
    if (!user?.name) return '?';
    return user.name.charAt(0).toUpperCase();
  };
  
  const getUserAvatarGradient = () => {
    // Orange gradient to match logo
    return 'from-orange-400 to-orange-600';
  };

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile dropdown
      if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
      
      // Close credits dropdown
      if (showCreditsDropdown && !event.target.closest('.credits-dropdown-container')) {
        setShowCreditsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown, showCreditsDropdown, setShowCreditsDropdown]);

  return (
    <nav className="bg-gradient-to-r from-purple-400 via-blue-400 to-indigo-400 border-b border-purple-300/30 px-4 sm:px-6 lg:px-8 py-3 sticky top-0 z-[60] shadow-xl backdrop-blur-sm">
      <div className="max-w-7xl mx-auto">
        {/* Top Row - Logo and User - Better Spacing on Mobile */}
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          {/* Logo - Stacked on mobile, horizontal on desktop */}
          <button 
            onClick={() => window.location.reload()}
            className="hover:opacity-90 transition-opacity"
          >
            {/* Mobile: Stacked and centered */}
            <div className="flex flex-col items-center sm:hidden">
              <Logo className="w-8 h-8 flex-shrink-0 mb-1" />
              <h1 className="text-sm font-bold text-white tracking-tight text-center">
                Magic Moments
              </h1>
            </div>
            
            {/* Desktop: Horizontal layout */}
            <div className="hidden sm:flex items-center space-x-3">
              <Logo className="w-10 h-10 flex-shrink-0" />
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">
                  {t('landing.app_name')}
                </h1>
                <p className="text-xs text-purple-100/80">{t('nav.tagline')}</p>
              </div>
            </div>
          </button>
        
          {/* User section - Professional Design with balanced mobile spacing */}
          {user && (
            <div className="flex items-center space-x-3 sm:space-x-4 md:space-x-5">
              {/* Cart Icon - Refined */}
              <button
                onClick={() => {
                  if (window.openCartModal) {
                    window.openCartModal();
                  }
                }}
                className="relative p-1.5 sm:p-2 hover:bg-white/20 rounded-lg sm:rounded-xl transition-all duration-200"
                title="Shopping Cart"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {/* Cart Count Badge - Professional */}
                {window.cartCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg ring-2 ring-white">
                    {window.cartCount}
                  </div>
                )}
              </button>
              
              {/* Credits - Professional Pill */}
              <div className="relative credits-dropdown-container">
                <button
                  onClick={() => setShowCreditsDropdown(!showCreditsDropdown)}
                  className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/30 hover:bg-white/30 transition-all duration-200 cursor-pointer shadow-lg"
                >
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-sm"></div>
                  <span className="text-sm font-semibold text-white">{user.credits?.toFixed(2) || 0}</span>
                  <span className="text-xs text-white/90 hidden sm:inline">{t('nav.credits')}</span>
                  <svg className="w-3 h-3 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showCreditsDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                    <button
                      onClick={() => {
                        onCreditsClick('buy');
                        setShowCreditsDropdown(false);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <svg className="w-5 h-5 text-purple-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" fill="currentColor"/>
                        <circle cx="12" cy="12" r="8.5" fill="white"/>
                        <text x="12" y="17" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="currentColor" text-anchor="middle">$</text>
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-gray-700">{t('credits.buy_credits')}</span>
                    </button>
                    <button
                      onClick={() => {
                        onCreditsClick('earn');
                        setShowCreditsDropdown(false);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-gray-700">{t('earn_credits.title')}</span>
                    </button>
                    <button
                      onClick={() => {
                        onCreditsClick('redeem');
                        setShowCreditsDropdown(false);
                      }}
                      className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3"
                    >
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                      <span className="text-xs sm:text-sm font-medium text-gray-700">Redeem Gift Credits</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Profile Dropdown - Professional Design */}
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center space-x-2 p-1.5 rounded-full hover:bg-white/20 transition-all duration-200"
                >
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br ${getUserAvatarGradient()} border-2 border-white/50 shadow-md flex items-center justify-center`}
                  >
                    <span className="text-white font-bold text-sm sm:text-base">
                      {getUserInitial()}
                    </span>
                  </div>
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showProfileDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    
                    {/* Menu Items */}
                    <button
                      onClick={() => {
                        onProfileClick();
                        setShowProfileDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center space-x-3 transition-colors"
                    >
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">{t('nav.my_profile')}</span>
                    </button>
                    
                    {/* My Orders - For ALL users */}
                    <button
                      onClick={() => {
                        setCurrentView('my-orders');
                        setShowProfileDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center space-x-3 transition-colors"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">{t('nav.my_orders')}</span>
                    </button>
                    
                    {/* My Referrals - For ALL users */}
                    <button
                      onClick={() => {
                        setShowReferralDashboard(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-purple-50 flex items-center space-x-3 transition-colors"
                    >
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">{t('nav.my_referrals')}</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowSupport(true);
                        setShowProfileDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-amber-50 flex items-center space-x-3 transition-colors"
                    >
                      <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">{t('nav.help_support')}</span>
                    </button>
                    
                    {/* Orders Dashboard - Admin Only */}
                    {user?.email === 'brianjamessurvey@gmail.com' && (
                      <>
                        <div className="border-t border-gray-100 my-1"></div>
                        <div className="px-4 py-2">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Admin Tools</p>
                        </div>
                        <div className="flex items-center">
                          <button
                            onClick={(e) => {
                              if (e.shiftKey) {
                                window.open('/my-orders', '_blank');
                              } else {
                                setShowOrdersDashboard(true);
                              }
                              setShowProfileDropdown(false);
                            }}
                            className="flex-1 px-4 py-2.5 text-left hover:bg-purple-50 flex items-center space-x-3 transition-colors"
                          >
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                            <span className="text-sm font-medium text-gray-700">Orders Dashboard</span>
                          </button>
                          <button
                            onClick={() => {
                              window.open('/my-orders', '_blank');
                              setShowProfileDropdown(false);
                            }}
                            className="px-2 py-2.5 hover:bg-purple-50 transition-colors"
                            title="Open in new tab"
                          >
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                    
                    {/* Admin Panel - Admin Only */}
                    {user?.email === 'brianjamessurvey@gmail.com' && (
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            if (e.shiftKey) {
                              window.open('/admin', '_blank');
                            } else {
                              setShowAdminPanel(true);
                            }
                            setShowProfileDropdown(false);
                          }}
                          className="flex-1 px-4 py-2.5 text-left hover:bg-red-50 flex items-center space-x-3 transition-colors"
                        >
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">Admin Panel</span>
                        </button>
                        <button
                          onClick={() => {
                            window.open('/admin', '_blank');
                            setShowProfileDropdown(false);
                          }}
                          className="px-2 py-2.5 hover:bg-red-50 transition-colors"
                          title="Open in new tab"
                        >
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    {/* Monitoring Dashboard - Admin Only */}
                    {user?.email === 'brianjamessurvey@gmail.com' && (
                      <div className="flex items-center">
                        <button
                          onClick={() => {
                            window.open('/admin/metrics', '_blank');
                            setShowProfileDropdown(false);
                          }}
                          className="flex-1 px-4 py-2.5 text-left hover:bg-blue-50 flex items-center space-x-3 transition-colors"
                        >
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-700">Monitoring Dashboard</span>
                        </button>
                        <button
                          onClick={() => {
                            window.open('/admin/metrics', '_blank');
                            setShowProfileDropdown(false);
                          }}
                          className="px-2 py-2.5 hover:bg-blue-50 transition-colors"
                          title="Open in new tab"
                        >
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </div>
                    )}
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Language Switcher - Hidden for now, focusing on growth */}
                    {/* <LanguageSwitcherCompact /> */}
                    
                    {/* <div className="border-t border-gray-100 my-1"></div> */}
                    
                    <button
                      onClick={() => {
                        onLogout();
                        setShowProfileDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left hover:bg-red-50 flex items-center space-x-3 transition-colors group"
                    >
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="text-sm font-medium text-red-600">Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Navigation Tabs Row - Professional Design with improved mobile spacing */}
        <div className="flex justify-center scrollbar-hide mt-3 sm:mt-0" style={{overflow: 'visible'}}>
          <div className="inline-flex items-center space-x-2 sm:space-x-1.5 bg-white/10 px-2 py-1.5 rounded-2xl shadow-lg border border-white/20 overflow-visible">
            {/* Shop Tab - First Position */}
            <button
              onClick={() => {
                setCurrentView('shop');
                // Scroll to top when switching to Shop tab
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className={`group relative px-3 py-2.5 rounded-xl font-medium transition-all duration-200 text-sm ${
                currentView === 'shop'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out">Shop</span>
              </span>
            </button>

            {/* Pictures Tab */}
            <button
              onClick={() => {
                setCurrentView('pictures');
                // Scroll to top of the page
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className={`group relative px-3 py-2.5 rounded-xl font-medium transition-all duration-200 text-sm ${
                currentView === 'pictures'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out">Pictures</span>
              </span>
            </button>

            {/* Animated Moments Tab */}
            <button
              onClick={() => {
                setCurrentView('living_memories');
                setShowPendingSubtab(false);
                setShowFailedSubtab(false);
                // Scroll to top of the page
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className={`group relative px-3 py-2.5 rounded-xl font-medium transition-all duration-200 text-sm ${
                currentView === 'living_memories'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out">Moments</span>
              </span>
            </button>

            {/* Collaborate Tab (Active) */}
            <button
              onClick={() => {
                setCurrentView('projects');
                // Scroll to top when switching to Collaborate tab
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
              className={`group relative px-3 py-2.5 rounded-xl font-medium transition-all duration-200 text-sm z-[70] ${
                currentView === 'projects'
                  ? 'bg-white text-purple-600 shadow-lg'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out">Collaborate</span>
              </span>
              
              {/* Combined badge for pending invitations + access requests */}
              {(() => {
                const totalCount = pendingInvitationsCount + totalPendingAccessRequests;
                console.log('🎨 RENDERING Badge - pendingInvitations:', pendingInvitationsCount, 'totalAccessRequests:', totalPendingAccessRequests, 'total:', totalCount, 'shouldShow:', totalCount > 0);
                return totalCount > 0 ? (
                  <>
                    {/* White backdrop circle */}
                    <div 
                      className="absolute rounded-full" 
                      style={{
                        top: '-10px',
                        right: '-10px',
                        width: '28px',
                        height: '28px',
                        backgroundColor: 'white',
                        zIndex: 9998
                      }}
                    />
                    {/* Red badge on top */}
                    <div 
                      className="absolute bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse" 
                      style={{
                        top: '-9px',
                        right: '-9px',
                        zIndex: 9999,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
                      }}
                    >
                      {totalCount}
                    </div>
                  </>
                ) : null;
              })()}
            </button>

          </div>
        </div>
      </div>
    </nav>
  );
};

export default MemoryNavbar;
